import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, MailCheck } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
  ok:   { Icon: CheckCircle2,  cls: "text-positive",     label: "OK" },
  warn: { Icon: AlertTriangle, cls: "text-secondary",    label: "Let op" },
  fail: { Icon: XCircle,       cls: "text-destructive",  label: "Fout" },
};

export default function SystemCheckBlock() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get("/admin/system-check", { params: { api_base: API_BASE } });
      setResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Controle mislukt.");
    }
    setBusy(false);
  }, []);

  useEffect(() => { run(); }, [run]);

  const [mailBusy, setMailBusy] = useState(false);
  const [mailResult, setMailResult] = useState(null);
  const testMail = async () => {
    setMailBusy(true);
    try {
      const { data } = await api.post("/admin/system-check/test-mail");
      setMailResult({ ok: true, text: `Testmail verstuurd naar ${data.to} (${data.sent_at}). Check je inbox.` });
      toast.success(`Testmail verstuurd naar ${data.to}`);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Testmail mislukt.";
      setMailResult({ ok: false, text: msg });
      toast.error(msg);
    }
    setMailBusy(false);
  };

  const s = result?.summary;
  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="system-check-block">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Systeemcheck</h2>
          <p className="text-xs text-muted-foreground mt-1">Controleert live of domein, webhook, Mollie-profiel en mail goed op elkaar aansluiten.</p>
        </div>
        <div className="flex items-center gap-3">
          {s && (
            <div className="flex items-center gap-2 text-xs font-bold" data-testid="system-check-summary">
              <span className="text-positive">{s.ok} OK</span>
              <span className="text-secondary">{s.warn} let op</span>
              <span className="text-destructive">{s.fail} fout</span>
            </div>
          )}
          <button onClick={testMail} disabled={mailBusy} data-testid="system-check-test-mail" title="Stuurt een testmail via Resend naar het beheer-adres"
            className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {mailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailCheck className="w-3.5 h-3.5" />} Testmail sturen
          </button>
          <button onClick={run} disabled={busy} data-testid="system-check-rerun"
            className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Opnieuw controleren
          </button>
        </div>
      </div>
      {mailResult && (
        <div className={`px-5 py-2 text-xs border-b-2 border-foreground ${mailResult.ok ? "bg-positive/20" : "bg-destructive/10 text-destructive"}`} data-testid="system-check-test-mail-result">
          {mailResult.text}
        </div>
      )}
      {error && <div className="p-4 text-sm text-destructive" data-testid="system-check-error">{error}</div>}
      {!result && !error && <div className="p-8 text-center text-muted-foreground text-sm">Controleren…</div>}
      {result && (
        <ul className="divide-y divide-border" data-testid="system-check-list">
          {result.checks.map((c) => {
            const { Icon, cls, label } = ICONS[c.status] || ICONS.warn;
            return (
              <li key={c.key} className="p-4 flex gap-3 items-start" data-testid={`system-check-${c.key}`} data-status={c.status}>
                <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${cls}`} />
                <div className="min-w-0">
                  <div className="text-sm font-bold flex items-center gap-2">
                    {c.label}
                    <span className={`text-[10px] uppercase tracking-widest ${cls}`}>{label}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 break-words">{c.detail}</div>
                  {Array.isArray(c.methods) && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mollie-methods-chips">
                      {c.methods.map((m) => (
                        <span key={m} data-testid={`mollie-method-${m}`}
                          className={`px-1.5 py-0.5 border-2 border-foreground text-[10px] uppercase tracking-widest font-bold ${m === "paybybank" ? "bg-destructive text-white" : "bg-positive/30"}`}>{m}</span>
                      ))}
                      <span data-testid="mollie-method-paybybank-status"
                        className={`px-1.5 py-0.5 border-2 text-[10px] uppercase tracking-widest font-bold ${c.paybybank ? "border-destructive text-destructive" : "border-dashed border-foreground/40 text-muted-foreground"}`}>
                        Pay by Bank: {c.paybybank ? "AAN" : "uit"}
                      </span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {result && (
        <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border">
          Laatst gecontroleerd: {new Date(result.checked_at).toLocaleString("nl-NL")}
        </div>
      )}
    </section>
  );
}
